import { z } from 'zod';

import { metadata } from '@nesso/common-contracts/schema-metadata';
import { treatmentApproachesSchema as baseTreatmentApproachesSchema } from '@nesso/common-contracts/treatment-approaches';

import {
    identifierSchema as baseIdentifierSchema,
    noteFormatSchema as baseNoteFormatSchema,
    noteLengthSchema as baseNoteLengthSchema,
    noteTemplateSchema,
} from './practice-member.models';

// One entry per settings type: a type literal and its value schema.
export const PRACTICE_MEMBER_SETTINGS_TYPES = ['notes', 'telehealth'] as const;

export const NOTES_SETTINGS_TYPE = 'notes' as const;
export const TELEHEALTH_SETTINGS_TYPE = 'telehealth' as const;
export const TELEHEALTH_LINK_TYPES = ['SAME_FOR_ALL', 'PER_APPOINTMENT'] as const;

// Message-carrying variants for onboarding forms; the plain enums carry no message.
export const noteFormatSchema = z.enum(baseNoteFormatSchema.options, {
    message: 'Choose the format you prefer',
});
export const noteLengthSchema = z.enum(baseNoteLengthSchema.options, {
    message: 'Choose the note length you prefer',
});
export const identifierSchema = z.enum(baseIdentifierSchema.options, {
    message: 'Choose the identifier you prefer',
});
export const treatmentApproachesSchema = baseTreatmentApproachesSchema.min(1, {
    message: 'Select at least 1 treatment approach',
});

// This is the complete shape a read returns: a stored row holds only the fields a member
// chose, with defaults filling the rest; a write validates per field, never this whole shape.
// `.min(1)` above is a form rule, not a storage one: onboarding writes before a pick is made.
export const notesSettingsValueSchema = z.object({
    noteFormat: baseNoteFormatSchema.meta(metadata.nonSensitive),
    noteLength: baseNoteLengthSchema.meta(metadata.nonSensitive),
    identifier: baseIdentifierSchema.meta(metadata.nonSensitive),
    treatmentApproaches: baseTreatmentApproachesSchema
        .refine(
            (values) => new Set(values).size === values.length,
            'treatmentApproaches must not contain duplicates',
        )
        .meta(metadata.nonSensitive),
    defaultNoteTemplate: noteTemplateSchema.meta(metadata.nonSensitive),
    includeCpt90837Justification: z.boolean().meta(metadata.nonSensitive),
    includeDiagnosisCodes: z.boolean().meta(metadata.nonSensitive),
});

export type NotesSettingsValue = z.infer<typeof notesSettingsValueSchema>;

export const notesSettingsPatchSchema = notesSettingsValueSchema.partial();

export type NotesSettingsPatch = z.infer<typeof notesSettingsPatchSchema>;

// Empty, malformed and non-https links deliberately share one message rather than a distinct
// insecure-link string.
// zod 4 still runs this `.refine()` when the piped `z.url()` already failed, so a scheme-less
// or unparseable string reaches it — the try/catch is what turns that into a failed refinement
// instead of an uncaught TypeError from `new URL`.
export const telehealthLinkSchema = z
    .string()
    .trim()
    .min(1, 'Enter a valid link')
    .pipe(z.url({ message: 'Enter a valid link' }))
    .refine(
        (value) => {
            try {
                return new URL(value).protocol === 'https:';
            } catch {
                return false;
            }
        },
        { message: 'Enter a valid link' },
    );

// Flat nullable fields rather than a discriminated union on `linkType`. The aggregate's
// `merge()` is `{ ...stored, ...patch }`, so switching SAME_FOR_ALL -> PER_APPOINTMENT under a
// union would leave a stale `link` in the stored jsonb; writing all three fields every time keeps
// the merge landing a complete, consistent value. `linkType: null` is "not configured" — ADR-0017
// removed the delete operation, so there is no row-absence state to fall back on.
// `link` and `instructions` carry no `metadata.nonSensitive` tag and must not get one: redaction
// is deny-by-default, so leaving them untagged is what keeps them out of the audit log
// (ASR-0017-06). Only `linkType` is tagged.
export const telehealthSettingsValueSchema = z
    .object({
        linkType: z.enum(TELEHEALTH_LINK_TYPES).nullable().meta(metadata.nonSensitive),
        link: telehealthLinkSchema.nullable(),
        instructions: z.string().nullable(),
    })
    .refine(
        (value) =>
            value.linkType === 'SAME_FOR_ALL'
                ? value.link !== null
                : value.link === null && value.instructions === null,
        'A telehealth link and instructions belong to SAME_FOR_ALL only',
    );

export type TelehealthSettingsValue = z.infer<typeof telehealthSettingsValueSchema>;
